<template>
  <transition name="accordion-slide">
    <tr v-if="isExpanded" class="accordion-content-row">
      <td :colspan="columnCount" class="expanded-row pa-0">
        <!-- Shimmer loading skeleton -->
        <div v-if="isLoading" class="accordion-shimmer-container">
          <div class="shimmer-row">
            <div class="shimmer-cell" v-for="n in 4" :key="'shimmer-header-'+n"></div>
          </div>
          <div class="shimmer-row">
            <div class="shimmer-bar" v-for="n in 4" :key="'shimmer-bar-'+n"></div>
          </div>
        </div>
        
        <!-- Actual content with animated stacked bar chart -->
        <div v-else class="accordion-chart-container" :data-row-id="rowData._id || rowIndex">
          <div class="chart-grid-table-wrapper">
            <table class="chart-table">
              <thead>
                <tr>
                  <th v-for="row in workflowRows" :key="row.label" class="workflow-body-data">{{ row.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="(row, rowIndex) in workflowRows" :key="`val-${row.label}`">
                    <div class="stacked-bar-wrapper">
                      <div class="stacked-bar" :class="{ 'animate-bar': summaryLoaded }">
                        <div
                          v-for="(s, sIndex) in row.statuses"
                          :key="s.label"
                          class="bar-segment"
                          :style="{
                            width: s.percent + '%',
                            backgroundColor: s.color,
                            animationDelay: (rowIndex * 0.1 + sIndex * 0.05) + 's'
                          }"
                          @mouseenter="showTooltip($event, s, row)"
                          @mouseleave="hideTooltip"
                        >
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Legend -->
          <div class="chart-legend">
            <div 
              v-for="status in workflowLegend" 
              :key="status.label" 
              class="legend-item"
            >
              <span class="legend-dot" :style="{ backgroundColor: status.color }"></span>
              <span class="legend-label">{{ status.label }}</span>
            </div>
          </div>
          
          <!-- Custom Tooltip - scoped to this row -->
          <div 
            v-if="tooltipVisible" 
            class="custom-chart-tooltip chat-bubble"
            :style="{ top: tooltipY + 'px', left: tooltipX + 'px' }"
          >
            <div class="tooltip-title">{{ tooltipData.label }}</div>
            <div class="tooltip-value">{{ tooltipData.value }} / {{ tooltipData.total }}</div>
            <div class="tooltip-percent">{{ tooltipData.percent }}%</div>
            <div class="bubble-arrow"></div>
          </div>
        </div>
      </td>
    </tr>
  </transition>
</template>

<script>
export default {
  name: 'WorkflowAccordion',
  props: {
    isExpanded: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    summaryLoaded: {
      type: Boolean,
      default: false
    },
    rowData: {
      type: Object,
      required: true
    },
    rowIndex: {
      type: [String, Number],
      required: true
    },
    columnCount: {
      type: Number,
      default: 6
    }
  },
  data() {
    return {
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipData: {
        label: '',
        value: 0,
        total: 0,
        percent: 0
      }
    };
  },
  computed: {
    workflowRows() {
      const v = this.rowData;
      const rows = [
        {
          label: "Local Comparative 1",
          values: {
            pending: v.pending_local_proforma || 0,
            awaiting: v.pendingapproval_local_proforma || 0,
            approved: v.approved_local_proforma || 0,
            rejected: v.rejected_local_proforma || 0,
            immaterial: v.immaterial_local_proforma || 0,
          }
        },
        {
          label: "Local Comparative 2",
          values: {
            pending: v.pending_local_forecast || 0,
            awaiting: v.pendingapproval_local_forecast || 0,
            approved: v.approved_local_forecast || 0,
            rejected: v.rejected_local_forecast || 0,
            immaterial: v.immaterial_local_forecast || 0,
          }
        },
        {
          label: "Regional Comparative 1",
          values: {
            pending: v.pending_regional_proforma || 0,
            awaiting: v.pendingapproval_regional_proforma || 0,
            approved: v.approved_regional_proforma || 0,
            rejected: v.rejected_regional_proforma || 0,
            immaterial: v.immaterial_regional_proforma || 0,
          }
        },
        {
          label: "Regional Comparative 2",
          values: {
            pending: v.pending_regional_forecast || 0,
            awaiting: v.pendingapproval_regional_forecast || 0,
            approved: v.approved_regional_forecast || 0,
            rejected: v.rejected_regional_forecast || 0,
            immaterial: v.immaterial_regional_forecast || 0,
          }
        }
      ];

      return rows.map(r => {
        const realTotal = Object.values(r.values).reduce((a, b) => a + b, 0);
        const calcTotal = realTotal || 1;
        return {
          label: r.label,
          total: realTotal,
          statuses: [
            { label: "Commentary Awaited", value: r.values.pending, color: "#3b82f6" },
            { label: "Awaiting Approval", value: r.values.awaiting, color: "#fbbf24" },
            { label: "Approved", value: r.values.approved, color: "#22c55e" },
            { label: "Rejected", value: r.values.rejected, color: "#ef4444" },
            { label: "Immaterial", value: r.values.immaterial, color: "#9ca3af" }
          ].map(s => ({
            ...s,
            percent: ((s.value / calcTotal) * 100).toFixed(0)
          }))
        };
      });
    },
    workflowLegend() {
      return [
        { label: "Commentary Awaited", color: "#3b82f6" },
        { label: "Awaiting Approval", color: "#fbbf24" },
        { label: "Approved", color: "#22c55e" },
        { label: "Rejected", color: "#ef4444" },
        { label: "Immaterial", color: "#9ca3af" }
      ];
    }
  },
  methods: {
    showTooltip(event, status, row) {
      const rect = event.target.getBoundingClientRect();
      const containerRect = event.target.closest('.accordion-chart-container')?.getBoundingClientRect() || { left: 0, top: 0 };
      
      this.tooltipX = rect.left - containerRect.left + (rect.width / 2);
      this.tooltipY = rect.top - containerRect.top - 10;
      this.tooltipData = {
        label: status.label,
        value: this.$nova.formatNumber(status.value),
        total: this.$nova.formatNumber(row.total),
        percent: status.percent
      };
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    }
  }
};
</script>

<style scoped>
/* Accordion slide animation */
.accordion-slide-enter-active,
.accordion-slide-leave-active {
  transition: all 0.3s ease-out;
  overflow: hidden;
}

.accordion-slide-enter,
.accordion-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.accordion-slide-enter-to,
.accordion-slide-leave {
  opacity: 1;
  transform: translateY(0);
}

.accordion-content-row td {
  transition: padding 0.3s ease;
}

/* Shimmer loading skeleton styles */
.accordion-shimmer-container {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.shimmer-row {
  display: flex;
  gap: 24px;
}

.shimmer-cell {
  height: 20px;
  flex: 1;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.shimmer-bar {
  height: 12px;
  flex: 1;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.theme--dark .shimmer-cell,
.theme--dark .shimmer-bar {
  background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
  background-size: 200% 100%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Animated Stacked Bar Chart Styles */
.accordion-chart-container {
  position: relative;
  background: #fafafa;
  border-radius: 0 0 8px 8px;
}

.theme--dark .accordion-chart-container {
  background: #1a1a2e;
}

.chart-grid-table-wrapper {
  width: 100%;
}

.chart-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.chart-table th {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  padding-bottom: 8px;
}

.theme--dark .chart-table th {
  color: #e5e7eb;
}

.chart-table td {
  vertical-align: top;
  padding: 40px 12px !important;
}

.stacked-bar-wrapper {
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.theme--dark .stacked-bar-wrapper {
  background: #374151;
}

.stacked-bar {
  display: flex;
  height: 100%;
  width: 100%;
}

.bar-segment {
  height: 100%;
  min-width: 0;
  transition: transform 0.2s ease;
  cursor: pointer;
  transform: scaleX(0);
  transform-origin: left;
}

.bar-segment:hover {
  filter: brightness(1.1);
  transform: scaleX(1) scaleY(1.1);
}

.animate-bar .bar-segment {
  animation: barGrow 0.6s ease-out forwards;
}

@keyframes barGrow {
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
}

/* Chart Legend Styles */
.chart-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
}

.theme--dark .chart-legend {
  border-top-color: #374151;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-label {
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
}

.theme--dark .legend-label {
  color: #9ca3af;
}

/* Custom Tooltip Styles - Chat Bubble */
.custom-chart-tooltip {
  position: absolute;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08);
  pointer-events: none;
  z-index: 100;
  transform: translate(-50%, -100%);
  min-width: 130px;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.25s ease, visibility 0.25s ease, transform 0.25s ease;
}

.custom-chart-tooltip.chat-bubble {
  opacity: 1;
  visibility: visible;
  animation: tooltipFadeIn 0.25s ease-out forwards;
}

.theme--dark .custom-chart-tooltip {
  background: #ffffff;
  border-color: #d1d5db;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* Chat bubble arrow */
.bubble-arrow {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #ffffff;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1));
}

.bubble-arrow::before {
  content: '';
  position: absolute;
  bottom: 1px;
  left: -9px;
  width: 0;
  height: 0;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid #ffffff;
  z-index: -1;
}

.tooltip-title {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.tooltip-value {
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 2px;
}

.tooltip-percent {
  font-size: 14px;
  font-weight: 700;
  color: #3b82f6;
}

@keyframes tooltipFadeIn {
  0% {
    opacity: 0;
    transform: translate(-50%, -100%) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -100%) scale(1);
  }
}

</style>
